/**
 * Where "pay now" goes, for every dunning surface. Two rules, because the two halves
 * of the feature send the customer to genuinely different places.
 *
 * Both diagram footers hang on one invariant — *payment stays open at every stage* —
 * so these routes are the most load-bearing links in the feature.
 */

/**
 * PAST DUE (the episode): the invoice list, for everyone.
 *
 * A constant rather than a ternary because the projection no longer tells the browser
 * whether an account is card- or invoice-billed, and it does not need to: each row
 * opens Stripe's hosted invoice page (open-api fills `receipt_url` from
 * `hosted_invoice_url`), and for an OPEN invoice that page takes the payment — by card
 * for a card customer, by the invoice's own method for a net-terms one.
 *
 * Sending everyone to the card form instead would put a card form in front of a
 * customer whose company pays by bank transfer against a PO, and a card added there
 * starts charging an account whose AP process never agreed to it.
 */
export const DUNNING_PAY_PATH = '/app/s/payment-history';

export function dunningPayPath(): string {
  return DUNNING_PAY_PATH;
}

/**
 * NOT YET DUE (the pre-emptive advisory): still two destinations.
 *
 * The advisory fires before anything has failed — "the card ending 4242 expires before
 * your next renewal", "your renewal invoice arrives on the 14th" — and it keeps the
 * `ladder` that says which. A card customer being warned about an expiring card needs
 * the CARD FORM; there is no unpaid invoice to send them to. A net-terms customer needs
 * the invoice list.
 */
export const ADVISORY_PAY_PATH_INVOICE = DUNNING_PAY_PATH;
export const ADVISORY_PAY_PATH_CARD = '/app/s/payment';

export function advisoryPayPath(isInvoiceBilled: boolean): string {
  return isInvoiceBilled ? ADVISORY_PAY_PATH_INVOICE : ADVISORY_PAY_PATH_CARD;
}

/** What the viewer is allowed to open, read from the same flags Settings uses. */
export interface PayPathAccess {
  isRootAdmin: boolean;
  /** `me.brandingData.hidePaymentHistoryPages` (white-label sub-flag). */
  hidePaymentHistoryPages: boolean;
  /** `me.brandingData.hideSubscriptionPages` (white-label sub-flag). */
  hideSubscriptionPages: boolean;
}

/**
 * Whether the viewer can actually USE a pay path, so no surface offers a button that
 * lands on "You do not have access to this page".
 *
 * Mirrors each destination's own Settings gate exactly (`SettingsLayout.tsx`), not one
 * rule for both: Billing History shows for `isRootAdmin && !hidePaymentHistoryPages`,
 * Payment Settings (the card form) for `isRootAdmin && !hideSubscriptionPages`. When this
 * is false the surface drops the button and shows the contact-your-admin line instead.
 */
export function canOpenPayPath(path: string, access: PayPathAccess): boolean {
  if (!access.isRootAdmin) return false;
  return path === ADVISORY_PAY_PATH_CARD
    ? !access.hideSubscriptionPages
    : !access.hidePaymentHistoryPages;
}
